import type { BandwidthCapacityDeductionCell, BandwidthGroupBreakdownRow } from './bandwidthDashboardTypes';
import { getBandwidthCapacityHoursForWorkLocation, getBandwidthGroupCapacityHours } from './bandwidthUtilization';
import { roundBwDecimal } from './bandwidthFormat';

/** Time entry types whose hours reduce bandwidth capacity and are excluded from logged/billable totals. */
export const BANDWIDTH_CAPACITY_DEDUCTION_ACTIVITY_TYPES = ['LEAVE', 'PUBLIC_HOLIDAY'] as const;

export type BandwidthCapacityDeductionActivityType =
  (typeof BANDWIDTH_CAPACITY_DEDUCTION_ACTIVITY_TYPES)[number];

const DEDUCTION_TYPE_SET = new Set<string>(BANDWIDTH_CAPACITY_DEDUCTION_ACTIVITY_TYPES);

export function isBandwidthCapacityDeductionType(type: string | undefined | null): boolean {
  if (!type) return false;
  return DEDUCTION_TYPE_SET.has(type);
}

export function buildCapacityDeductionLookups(cells: BandwidthCapacityDeductionCell[] | undefined): {
  byGroupUser: Map<string, number>;
  byUser: Map<string, number>;
} {
  const byGroupUser = new Map<string, number>();
  const byUser = new Map<string, number>();
  for (const cell of cells ?? []) {
    const key = `${cell.groupId}\t${cell.userId}`;
    byGroupUser.set(key, (byGroupUser.get(key) ?? 0) + cell.deductionHours);
    byUser.set(cell.userId, (byUser.get(cell.userId) ?? 0) + cell.deductionHours);
  }
  return { byGroupUser, byUser };
}

export function getCapacityDeductionHoursForGroupUser(
  lookups: { byGroupUser: Map<string, number> },
  groupId: string,
  userId: string
): number {
  return lookups.byGroupUser.get(`${groupId}\t${userId}`) ?? 0;
}

/** Tailwind classes for Leave / Public Holiday rows in the Teams table. */
export const BW_TEAMS_DEDUCTION_ROW_CLASS = 'text-red-700 font-medium';

export function getEffectiveBandwidthCapacityHours(
  baseCapacityHours: number,
  deductionHours: number
): number {
  return roundBwDecimal(Math.max(0, baseCapacityHours - deductionHours));
}

export function getGroupEffectiveCapacityHours(
  g: BandwidthGroupBreakdownRow,
  dateFrom: string,
  dateTo: string,
  deductionLookups: ReturnType<typeof buildCapacityDeductionLookups>
): number {
  const capPerPerson = getBandwidthCapacityHoursForWorkLocation(dateFrom, dateTo, g.workLocation);
  if (g.users.length === 0) {
    return getBandwidthGroupCapacityHours(dateFrom, dateTo, g);
  }
  let cap = 0;
  for (const user of g.users) {
    const deductionHours = getCapacityDeductionHoursForGroupUser(deductionLookups, g.groupId, user.userId);
    cap += getEffectiveBandwidthCapacityHours(capPerPerson, deductionHours);
  }
  return cap;
}
